import React, { useEffect, useRef, useState } from "react";
import { isMobile } from "react-device-detect";
import {
  getStudyPreloadProgress,
  preloadStudySeries,
  stopStudyPreload,
} from "../../helpers/CornerstoneThumbnailService";
import "../../styles/styles.css";

// How often the button re-reads progress while it is downloading.
const PROGRESS_TICK_MS = 1000;
const IDLE_TICK_MS = 3000;

const DownloadSVG = () => (
  <svg width="11" height="11" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M8 1v9m0 0L4.5 6.5M8 10l3.5-3.5M2 12v2.5h12V12" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const CheckSVG = () => (
  <svg width="11" height="11" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M2.5 8.5l3.5 3.5 7.5-8" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/**
 * Study header button: preload every series of this study that isn't loaded
 * yet (cine clips with all their frames). Click again while running to stop.
 * entries: [{ index, stack }] — the thumbnail index each series uses.
 */
const StudyPreloadButton = ({ entries }) => {
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState(() => getStudyPreloadProgress(entries));
  const controllerRef = useRef(null);
  const entriesRef = useRef(entries);
  entriesRef.current = entries;

  // The study's series can arrive after the header renders (priors, shared
  // links): re-read progress whenever they change, never trust the first read.
  const entriesKey = entries
    .map(({ index, stack }) => `${index}:${stack?.imageIds?.length ?? 0}`)
    .join(",");
  useEffect(() => {
    setProgress(getStudyPreloadProgress(entriesRef.current));
  }, [entriesKey]);

  // Stop our run if the viewer closes or the study changes under us.
  useEffect(() => () => {
    if (controllerRef.current) stopStudyPreload(controllerRef.current);
  }, []);

  // Idle too, more slowly: the background preload and viewports fill series in
  // on their own, and the memory cache can drop images again, so ✓ is kept
  // honest rather than latched.
  useEffect(() => {
    const timer = setInterval(
      () => setProgress(getStudyPreloadProgress(entriesRef.current)),
      running ? PROGRESS_TICK_MS : IDLE_TICK_MS
    );
    return () => clearInterval(timer);
  }, [running]);

  // Nothing to preload (yet): no button rather than a false ✓.
  if (isMobile || progress == null) return null;
  const finished = !running && progress >= 1;

  const start = async () => {
    const controller = { abort: false };
    controllerRef.current = controller;
    setRunning(true);
    try {
      await preloadStudySeries(entriesRef.current, controller);
    } finally {
      if (controllerRef.current === controller) {
        controllerRef.current = null;
        setRunning(false);
      }
      setProgress(getStudyPreloadProgress(entriesRef.current));
    }
  };

  const stop = () => {
    if (controllerRef.current) stopStudyPreload(controllerRef.current);
    controllerRef.current = null;
    setRunning(false);
    setProgress(getStudyPreloadProgress(entriesRef.current));
  };

  const done = finished;
  const pct = Math.floor(progress * 100);
  const title = done
    ? "Study preloaded (click to check again)"
    : running
      ? `Preloading study… ${pct}% (click to stop)`
      : "Preload study (all series and clip frames)";

  return (
    <span
      className={`study-preload-button${running ? " study-preload-running" : ""}${done ? " study-preload-done" : ""}`}
      title={title}
      role="button"
      data-testid="study-preload-button"
      onClick={(e) => {
        e.stopPropagation();
        if (running) stop();
        else start();
      }}
    >
      {done ? <CheckSVG /> : <DownloadSVG />}
      {running && <span className="study-preload-pct">{pct}%</span>}
    </span>
  );
};

export default StudyPreloadButton;
